/**
 * <cart-upsell>
 *
 * AfterSell Strategies-powered cart drawer upsell. Renders nothing until a
 * successful, non-empty recommendation response is in hand, and never
 * blocks the rest of the cart drawer if AfterSell is unconfigured, empty,
 * or erroring.
 *
 * The accordion shell (title, chevron icons, expand/collapse) is rendered
 * server-side by `snippets/cart-upsell.liquid` via the existing
 * `shared-accordion` snippet. This module only fills the empty
 * `[data-cart-upsell-carousel]` placeholder inside it and reveals the
 * `<cart-upsell>` wrapper once there is something to show.
 *
 * Request/response shape and auth confirmed against AfterSell's published
 * OpenAPI spec (https://docs.aftersell.com/api/strategies.json):
 * base host `https://start.aftersell.app`, `X-Strategy-Api-Key` header auth,
 * a `{ shopDomain, strategyId, context: {...} }` request body, and a
 * `{ success, products, evaluationId, errors }` response body returned on
 * every status code (200/400/401/403/500) — so failures are read from the
 * parsed body, not just the HTTP status.
 */

const AFTERSELL_API_BASE_URL = 'https://start.aftersell.app';
const AFTERSELL_EVALUATE_PATH = '/api/public/strategy/evaluate';

let lastEvaluatedSignature = null;

/**
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * @param {string | null | undefined} json
 * @param {unknown} fallback
 * @returns {unknown}
 */
function safeParseJSON(json, fallback = null) {
  if (window.utilities?.safeParseJSON) return window.utilities.safeParseJSON(json, fallback);
  if (!json) return fallback;
  try {
    return JSON.parse(json);
  } catch (error) {
    return fallback;
  }
}

/**
 * Extracts the trailing numeric id from a Shopify GID
 * (`gid://shopify/ProductVariant/123` -> `"123"`), since `/cart/add.js`
 * expects the plain numeric variant id, not a GID.
 * @param {string | null | undefined} gid
 * @returns {string | null}
 */
function extractNumericIdFromGid(gid) {
  const match = String(gid ?? '').match(/(\d+)$/);
  return match ? match[1] : null;
}

/**
 * Builds the AfterSell Strategy evaluate request body from the current
 * Shopify cart (as returned by `/cart.js`). All cart line items are sent
 * as the products context, per AfterSell's cart-integration guidance.
 * Shape confirmed against AfterSell's published OpenAPI spec: ids are
 * Shopify GIDs, and `price`/`subtotal` are dollar amounts (cart.js
 * reports cents, hence the /100 conversions below).
 * @param {{ cart: object, customerId?: string | number | null, strategyId: string, shopDomain?: string }} args
 * @returns {object}
 */
export function buildEvaluatePayload({ cart, customerId, strategyId, shopDomain }) {
  const lineItems = Array.isArray(cart?.items) ? cart.items : [];

  const products = lineItems
    .filter((item) => item.product_id != null)
    .map((item) => ({
      productId: `gid://shopify/Product/${item.product_id}`,
      variantId: item.variant_id != null ? `gid://shopify/ProductVariant/${item.variant_id}` : undefined,
      quantity: item.quantity,
      price: item.price != null ? item.price / 100 : undefined,
      handle: item.handle,
      title: item.product_title,
      vendor: item.vendor,
    }));

  return {
    shopDomain,
    strategyId,
    context: {
      products,
      cartToken: cart?.token,
      cart: {
        subtotal: cart?.total_price != null ? cart.total_price / 100 : 0,
        itemCount: cart?.item_count ?? 0,
        lineCount: lineItems.length,
      },
      customer: customerId ? { customerId: `gid://shopify/Customer/${customerId}` } : undefined,
      localization: cart?.currency ? { currency: cart.currency } : undefined,
    },
  };
}

/**
 * Pure signature of "what's in the cart right now", used to avoid
 * redundant duplicate Strategy API calls when the cart hasn't materially
 * changed between two evaluate triggers.
 * @param {object} cart
 * @returns {string}
 */
export function computeCartSignature(cart) {
  const lineItems = Array.isArray(cart?.items) ? cart.items : [];
  const lineSignature = lineItems
    .map((item) => `${item.variant_id}:${item.quantity}`)
    .sort()
    .join(',');

  return `${lineSignature}|${cart?.item_count ?? 0}|${cart?.total_price ?? 0}`;
}

/**
 * @param {string | null} previousSignature
 * @param {string} nextSignature
 * @returns {boolean}
 */
export function shouldReEvaluate(previousSignature, nextSignature) {
  if (previousSignature == null) return true;
  return previousSignature !== nextSignature;
}

/**
 * @param {{ variantId: string | number, evaluationId: string | null, extraProperties?: object }} args
 * @returns {object}
 */
export function buildAddToCartPayload({ variantId, evaluationId, extraProperties = {} }) {
  return {
    items: [
      {
        id: variantId,
        quantity: 1,
        properties: {
          __as_offer_id: evaluationId,
          ...extraProperties,
        },
      },
    ],
  };
}

/**
 * Extracts the shopper's own selected size(s) already in their cart, so a
 * recommended product can be pre-selected to the same size instead of
 * whichever variant AfterSell happened to list first. Reads the same
 * `options_with_values` field, matched against the same translated size
 * option name (`window.product.size_label`), that
 * `snippets/cart-edit-size.liquid` already uses to find a line item's
 * size — kept name-scoped (not "any option value") so it can't
 * accidentally match on an unrelated option like color.
 * @param {object} cart - as returned by `/cart.js`.
 * @param {string} [sizeOptionName] - defaults to `window.product.size_label`.
 * @returns {string[]} unique size values present in the cart, lowercased.
 */
export function extractCartSizeLabels(cart, sizeOptionName = window.product?.size_label) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const normalizedOptionName = String(sizeOptionName ?? '').toLowerCase();
  const labels = new Set();

  items.forEach((item) => {
    (item.options_with_values || []).forEach((option) => {
      if (String(option?.name ?? '').toLowerCase() === normalizedOptionName && option.value) {
        labels.add(String(option.value).toLowerCase());
      }
    });
  });

  return [...labels];
}

/**
 * @param {object} variant
 * @param {string[]} cartSizeLabels - from `extractCartSizeLabels`, already lowercased.
 * @returns {boolean}
 */
function variantMatchesCartSize(variant, cartSizeLabels) {
  if (!cartSizeLabels?.length) return false;
  const values = (variant?.selectedOptions || []).map((option) => String(option?.value ?? '').toLowerCase());
  return cartSizeLabels.some((size) => values.includes(size));
}

/**
 * Maps one AfterSell response product (a `RecommendedProduct`, carrying a
 * nested `variants` array rather than a single resolved variant) into the
 * one variant the card renders. Prefers an available variant matching a
 * size already in the shopper's cart (see `extractCartSizeLabels`); falls
 * back to the first available variant, then the first variant outright,
 * since `filterAvailableRecommendations` still drops it downstream if
 * it's unavailable — "must not add unavailable variants" applies
 * regardless of which variant ends up picked here.
 * @param {object} raw
 * @param {string[]} [cartSizeLabels] - from `extractCartSizeLabels`.
 * @returns {{ variantId: string, title: string, imageUrl: string, price: unknown, compareAtPrice: unknown, sizeLabel: string, available: boolean } | null}
 */
export function normalizeRecommendation(raw, cartSizeLabels = []) {
  if (!raw) return null;

  const variants = Array.isArray(raw.variants) ? raw.variants : [];
  const sizeMatchedVariant = variants.find(
    (candidate) => candidate?.availableForSale && variantMatchesCartSize(candidate, cartSizeLabels),
  );
  const variant = sizeMatchedVariant || variants.find((candidate) => candidate?.availableForSale) || variants[0];

  const variantId = extractNumericIdFromGid(variant?.variantId);
  if (!variantId) return null;

  const imageUrl = variant?.image?.src || raw.images?.[0]?.src || '';
  const rawSizeLabel = variant?.selectedOptions?.map((option) => option.value).join(' / ') || variant?.title || '';
  // "Default Title" is Shopify's placeholder for a product with no real
  // options — not something a shopper should see as a "size".
  const sizeLabel = rawSizeLabel === 'Default Title' ? '' : rawSizeLabel;

  return {
    variantId,
    title: raw.title ?? '',
    imageUrl,
    price: variant?.price ?? raw.price ?? null,
    compareAtPrice: variant?.compareAtPrice ?? raw.compareAtPrice ?? null,
    sizeLabel,
    available: variant?.availableForSale !== false,
  };
}

/**
 * Drops unavailable/missing-variant recommendations and truncates to the
 * configured max — the enforcement point for "must not add unavailable
 * variants" and the max-recommendations theme setting.
 * @param {Array<object | null>} recommendations
 * @param {number} [maxProducts]
 * @returns {Array<object>}
 */
export function filterAvailableRecommendations(recommendations, maxProducts) {
  if (!Array.isArray(recommendations)) return [];

  const available = recommendations.filter((item) => item && item.available && item.variantId);

  return typeof maxProducts === 'number' ? available.slice(0, maxProducts) : available;
}

/**
 * Mirrors `snippets/cart-item-details.liquid`'s price markup exactly
 * (`cart-item__discounted-prices` / `cart-item__regular-prices`,
 * `cart-item__final-price`, `cart-item__old-price`, the same fluid
 * typography classes) so a recommended product's price reads identically
 * to a cart line item's price — same colors, same sizes, same
 * strikethrough treatment — rather than a parallel set of card-only
 * styles. `component-cart-items.css` is already loaded by
 * `cart-drawer.liquid`, so these classes are always available here.
 * @param {unknown} price
 * @param {unknown} compareAtPrice
 * @returns {string}
 */
function renderPriceHtml(price, compareAtPrice) {
  const currency = window.Shopify?.currency?.active;
  const format = (amount) => (window.utilities?.formatPrice ? window.utilities.formatPrice(currency, amount) : amount);

  const hasCompareAt = compareAtPrice != null && Number(compareAtPrice) > Number(price);

  if (hasCompareAt) {
    return `
      <div class="cart-upsell-card__price cart-item__discounted-prices">
        <span class="cart-item__final-price fluid-utility-l-em text-trim">${escapeHtml(format(price))}</span>
        <span class="cart-item__old-price fluid-speciality-markdown-price-m text-trim">${escapeHtml(format(compareAtPrice))}</span>
      </div>
    `;
  }

  return `
    <div class="cart-upsell-card__price cart-item__regular-prices">
      <span class="cart-item__final-price fluid-utility-l-em text-trim">${escapeHtml(format(price))}</span>
    </div>
  `;
}

/**
 * Generic fallback "add" icon (`assets/icon-plus.svg`) used only if the
 * Liquid config didn't supply the theme's actual per-brand quick-add bag
 * icon (see `addIconHtml` in `snippets/cart-upsell.liquid`). Static,
 * theme-authored markup — safe to inline directly, no escaping needed.
 */
const DEFAULT_ADD_ICON_HTML =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><polygon points="21 11 12.478515625 11 12.478515625 3 11.478515625 3 11.478515625 11 3 11 3 12 11.478515625 12 11.478515625 21 12.478515625 21 12.478515625 12 21 12 21 11" fill="currentColor" /></svg>';

/**
 * Builds one card's markup. All interpolated third-party text is escaped
 * before insertion via `innerHTML`.
 * @param {object} recommendation - as returned by `normalizeRecommendation`.
 * @param {{ addToCartAriaLabel?: string, sizeLabelPrefix?: string }} [strings]
 * @param {string} [iconHtml] - trusted, theme-rendered icon markup (not escaped).
 * @returns {string}
 */
export function renderCardHtml(recommendation, strings = {}, iconHtml = DEFAULT_ADD_ICON_HTML) {
  const { variantId, title, imageUrl, price, compareAtPrice, sizeLabel } = recommendation;
  const safeTitle = escapeHtml(title);

  // Not the silhouette metafield used for the cart line items' own subtitle:
  // AfterSell's response has no metafields field (documented as request-only,
  // never returned), so `productType` (Shopify's product_type) is the closest
  // available substitute — and per product feedback, not close enough to show.

  const sizeHtml = sizeLabel
    ? `<p class="cart-upsell-card__size fixed-utility-s">${escapeHtml(
        (strings.sizeLabelPrefix || 'Size [size]').replace('[size]', sizeLabel),
      )}</p>`
    : '';

  const addLabel = escapeHtml(
    (strings.addToCartAriaLabel || 'Add [product_title] to bag').replace('[product_title]', title),
  );

  return `
    <div class="cart-upsell-card" data-cart-upsell-card>
      <div class="cart-upsell-card__media">
        ${
          imageUrl
            ? `<img class="cart-upsell-card__image" src="${escapeHtml(imageUrl)}" alt="${safeTitle}" loading="lazy" width="100" height="100" />`
            : ''
        }
      </div>
      <div class="cart-upsell-card__details">
        <div class="cart-upsell-card__content">
          <div class="cart-upsell-card__title-text">
            <p class="cart-upsell-card__title cart-item__name fluid-utility-m text-trim">${safeTitle}</p>
          </div>
          ${renderPriceHtml(price, compareAtPrice)}
        </div>
        ${sizeHtml}
      </div>
      <button
        type="button"
        class="cart-upsell-card__add"
        data-cart-upsell-add
        data-variant-id="${escapeHtml(variantId)}"
        aria-label="${addLabel}"
      >
        ${iconHtml || DEFAULT_ADD_ICON_HTML}
      </button>
    </div>
  `;
}

/**
 * Loading placeholder shown only for the `defaultOpen` case, sized to
 * exactly match `.cart-upsell__slide`'s fixed row height (a horizontal
 * carousel's height doesn't depend on how many cards it ends up holding),
 * so swapping it for real cards once the evaluate call resolves causes no
 * layout shift — the row was already occupying that space. `aria-hidden`
 * since it carries no content worth announcing.
 * @returns {string}
 */
export function renderSkeletonHtml() {
  return `
    <div class="cart-upsell__skeleton" aria-hidden="true">
      <div class="cart-upsell__skeleton-media"></div>
      <div class="cart-upsell__skeleton-details">
        <div class="cart-upsell__skeleton-line cart-upsell__skeleton-line--wide"></div>
        <div class="cart-upsell__skeleton-line cart-upsell__skeleton-line--narrow"></div>
      </div>
    </div>
  `;
}

/**
 * Wraps rendered card slides in the `carousel-reel` markup shape expected
 * by `assets/shared-carousel-reel.js` (already loaded theme-wide as a
 * module script). Only ever inserted in one shot, with slides already
 * present, since `<carousel-reel>` never re-initializes an empty shell
 * populated later.
 * @param {string} cardsHtml
 * @returns {string}
 */
export function renderCarouselHtml(cardsHtml) {
  return `
    <carousel-reel mobileSlidesPerView="2.1" min-slides-drag="3">
      <div class="reel-slider swiper cart-upsell__carousel">
        <div class="swiper-wrapper">${cardsHtml}</div>
      </div>
    </carousel-reel>
  `;
}

/**
 * Single fetch wrapper for AfterSell's Strategy evaluate endpoint. Every
 * response — 200, 400, 401, 403, or 500 — returns a JSON body with a
 * `success` flag and, on failure, an `errors` array, so failures are read
 * from the parsed body rather than solely the HTTP status.
 * @param {object} payload
 * @param {{ strategyApiKey: string, baseUrl?: string }} options
 * @returns {Promise<object>}
 */
export async function evaluateStrategy(payload, { strategyApiKey, baseUrl = AFTERSELL_API_BASE_URL }) {
  const response = await fetch(`${baseUrl}${AFTERSELL_EVALUATE_PATH}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Strategy-Api-Key': strategyApiKey,
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (!response.ok || result?.success === false) {
    const message = result?.errors?.[0]?.message || `AfterSell evaluate failed: ${response.status}`;
    throw new Error(message);
  }

  return result;
}

class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this._config = null;
    this._evaluationId = null;
    this._isEvaluating = false;
    this._cartUpdateUnsubscriber = null;
  }

  /**
   * Deliberately does NOT call `evaluateAndRender()` here. `<cart-upsell>`
   * lives inside the cart drawer, which is rendered on every page whenever
   * the cart is non-empty — connecting on every page load, not just when
   * the shopper actually opens the drawer. Evaluating eagerly here would
   * fire a real third-party AfterSell request on every such page view,
   * most of which never open the drawer at all. Instead, `cart-drawer.js`
   * calls `evaluateAndRender()` explicitly from its own `open()`, so the
   * first call only happens once the drawer is actually shown. The
   * `cartUpdate` subscription below still covers re-evaluating for
   * quantity/removal changes made *inside* an already-open drawer, which
   * go through `CartDrawerItems.renderCartFromParsedState` and never call
   * `open()` again.
   */
  connectedCallback() {
    this._config = this._readConfig();
    if (!this._config) return;

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      this._cartUpdateUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        this.evaluateAndRender();
      });
    }
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsubscriber) {
      this._cartUpdateUnsubscriber();
      this._cartUpdateUnsubscriber = null;
    }
  }

  _readConfig() {
    const script = this.querySelector('script[data-cart-upsell-config]');
    return safeParseJSON(script?.textContent?.trim(), null);
  }

  get _placeholder() {
    return this.querySelector('[data-cart-upsell-carousel]');
  }

  /**
   * Fetches the current cart, decides whether AfterSell needs a fresh
   * evaluate call, and renders (or hides) the recommendation carousel.
   * Fire-and-forget from `connectedCallback` — must never block the rest
   * of the cart drawer's own render/open/refresh flow.
   */
  async evaluateAndRender() {
    const placeholder = this._placeholder;
    if (!this._config || !placeholder || this._isEvaluating) return;

    this._isEvaluating = true;

    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!cartResponse.ok) throw new Error(`cart.js failed: ${cartResponse.status}`);
      const cart = await cartResponse.json();

      const signature = computeCartSignature(cart);
      if (!shouldReEvaluate(lastEvaluatedSignature, signature)) {
        this._isEvaluating = false;
        return;
      }

      // Reserve the row's height and open the accordion immediately when
      // configured to default-open, rather than waiting on the evaluate
      // response — see `renderSkeletonHtml`. Only once we know a fresh
      // attempt is actually happening (past the dedupe check above), and
      // only when there's no content yet: a re-evaluation after a cart
      // change swaps straight from old cards to new ones instead of
      // flashing a skeleton in between.
      if (this._config.defaultOpen && !placeholder.hasChildNodes()) {
        placeholder.innerHTML = renderSkeletonHtml();
        this.hidden = false;
        this.querySelector('details')?.setAttribute('open', '');
      }

      const payload = buildEvaluatePayload({
        cart,
        customerId: this._config.customerId,
        strategyId: this._config.strategyId,
        shopDomain: window.Shopify?.shop,
      });

      const result = await evaluateStrategy(payload, { strategyApiKey: this._config.strategyApiKey });

      lastEvaluatedSignature = signature;

      const cartSizeLabels = extractCartSizeLabels(cart);
      const rawProducts = Array.isArray(result?.products) ? result.products : [];
      const recommendations = filterAvailableRecommendations(
        rawProducts.map((raw) => normalizeRecommendation(raw, cartSizeLabels)),
        this._config.maxProducts,
      );

      if (recommendations.length === 0) {
        this._hide(placeholder);
        return;
      }

      this._evaluationId = result?.evaluationId ?? null;

      const cardsHtml = recommendations
        .map(
          (recommendation) =>
            `<div class="swiper-slide cart-upsell__slide">${renderCardHtml(recommendation, this._config.strings, this._config.addIconHtml)}</div>`,
        )
        .join('');

      placeholder.innerHTML = renderCarouselHtml(cardsHtml);
      this.hidden = false;

      // The <details> always starts collapsed (see cart-upsell.liquid) to
      // avoid showing an empty-but-expanded accordion while this request
      // is in flight. Only now, with real content in place, do we honor
      // the merchant's "expand by default" setting.
      if (this._config.defaultOpen) {
        this.querySelector('details')?.setAttribute('open', '');
      }

      this._bindAddToCart(placeholder);
    } catch (error) {
      console.error('[cart-upsell] evaluate failed', error);
      this._hide(placeholder);
    } finally {
      this._isEvaluating = false;
    }
  }

  /**
   * @param {HTMLElement} placeholder
   */
  _hide(placeholder) {
    this.hidden = true;
    if (placeholder) placeholder.innerHTML = '';
  }

  /**
   * @param {HTMLElement} placeholder
   */
  _bindAddToCart(placeholder) {
    placeholder.querySelectorAll('[data-cart-upsell-add]').forEach((button) => {
      button.addEventListener('click', () => this._handleAdd(button));
    });
  }

  /**
   * @param {HTMLButtonElement} button
   */
  _handleAdd(button) {
    if (button.classList.contains('is-loading')) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const cart = document.querySelector('cart-drawer');
    const config =
      typeof window.fetchConfig === 'function' ? window.fetchConfig('javascript') : { method: 'POST', headers: {} };

    config.headers = {
      ...config.headers,
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    const payload = buildAddToCartPayload({ variantId, evaluationId: this._evaluationId });

    if (cart?.getSectionsToRender) {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
    }

    config.body = JSON.stringify(payload);
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');

    const cartAddUrl = this._config?.cartAddUrl || window.routes?.cart_add_url;

    fetch(cartAddUrl, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Unable to add to bag');
        }

        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        }

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-upsell',
            productVariantId: variantId,
            cartData: response,
          });
        }
      })
      .catch((error) => {
        console.error('[cart-upsell] add to cart failed', error);
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      });
  }
}

customElements.define('cart-upsell', CartUpsell);
